import { useEffect, useState, type ReactNode } from "react";
import { Spinner } from "@/components/ui/spinner";
import { Item, ItemContent, ItemDescription, ItemMedia, ItemTitle, ItemList } from "@/components/ui/item";
import { useAuth, useUser } from "@clerk/react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeftIcon,
  ArrowUpRightIcon,
  BlocksIcon,
  CopyIcon,
  HourglassIcon,
  MegaphoneIcon,
  MoreVerticalIcon,
  PlugIcon,
  PlusIcon,
  RefreshCwIcon,
  Trash2Icon,
  UnplugIcon,
} from "lucide-react";
import google from "@/assets/google.svg";
import gmail from "@/assets/gmail.svg";
import googleDocs from "@/assets/google_docs.svg";
import googleSheets from "@/assets/google_sheets.svg";
import composioBlack from "@/assets/composio_black.svg";
import composioWhite from "@/assets/composio_white.svg";
import uplodioOrange from "@/assets/uplodio_mark_orange.svg";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Frame,
  FrameDescription,
  FrameHeader,
  FramePanel,
  FrameTitle,
  FrameAction,
  FrameCount,
} from "@/components/ui/frame";
import EmptyState from "@/components/ui/empty-state";
import { DubIntegrationFrame } from "./DubIntegrationFrame";
import { TruncatedText } from "@/components/ui/truncated-text";
import { useIntegrationsStore } from "@/stores/eve/integrationsStore";
import type { EveAuthMode, EveIntegration } from "@/types/eve";

/**
 * Settings → Integrations: the org's Composio connected accounts — the
 * Gmail mailbox pool Amy sends from plus any other toolkit she works
 * through. Connect (a header button opens the toolkit picker; for Gmail it
 * also asks which OAuth app signs the user in — Composio's or Uplodio's
 * own Google client — and Composio's OAuth returns here with
 * `?status=success|failed`), reconnect (keeps the row's app), and remove
 * (org admins only; the server refuses while a live campaign still sends
 * from the mailbox or an enabled wait phase listens on the account). Which
 * mailbox a campaign uses is set per campaign, not here.
 */

const TOOLKIT_LABELS: Record<string, string> = {
  gmail: "Gmail",
  googlesheets: "Google Sheets",
  googledocs: "Google Docs",
  hubspot: "HubSpot",
  airtable: "Airtable",
  slack: "Slack",
  outlook: "Outlook",
  shopify: "Shopify",
};

/** One-liners for the picker; anything else falls back to a generic blurb. */
const TOOLKIT_BLURBS: Record<string, string> = {
  gmail: "A mailbox Amy sends campaign email from",
  googlesheets: "Read and update spreadsheets",
  googledocs: "Read documents",
};

/** Mirrors the server's DASHBOARD_TOOLKITS — rendered until the fetch replaces it. */
const DEFAULT_TOOLKITS = ["gmail", "googlesheets", "googledocs"];

/** Brand marks per toolkit; other google* slugs fall back to the G, everything else to a generic icon. */
const TOOLKIT_ICONS: Record<string, string> = {
  gmail,
  googlesheets: googleSheets,
  googledocs: googleDocs,
};

const toolkitLabel = (slug: string) => TOOLKIT_LABELS[slug] ?? slug;

/** Toolkits whose connect asks which OAuth app to sign in with (auth configs are per toolkit). */
const AUTH_MODE_TOOLKITS = new Set(["gmail"]);
const AUTH_MODE_OPTIONS: { value: EveAuthMode; label: string; description: string }[] = [
  {
    value: "composio",
    label: "Composio managed",
    description: "Sign in with Composio's Google app.",
  },
  {
    value: "uplodio",
    label: "Uplodio managed",
    description: "Sign in with Uplodio's own Google app.",
  },
];
const AUTH_MODE_LABELS: Record<EveAuthMode, string> = {
  composio: "via Composio",
  uplodio: "via Uplodio",
};
/**
 * Brand marks per auth mode. Composio is monochrome — black on light, white
 * on dark (same swap as the Dub tile); Uplodio is the brand orange
 * (#ff6900, the primary logo's fill) in both themes.
 */
const AUTH_MODE_MARKS: Record<EveAuthMode, { light: string; dark: string }> = {
  composio: { light: composioBlack, dark: composioWhite },
  uplodio: { light: uplodioOrange, dark: uplodioOrange },
};

function AuthModeMark({ mode, className }: { mode: EveAuthMode; className?: string }) {
  const mark = AUTH_MODE_MARKS[mode];
  if (mark.light === mark.dark) {
    return <img src={mark.light} alt="" className={cn("object-contain", className)} />;
  }
  return (
    <>
      <img src={mark.light} alt="" className={cn("object-contain dark:hidden", className)} />
      <img src={mark.dark} alt="" className={cn("hidden object-contain dark:block", className)} />
    </>
  );
}

type BadgeVariant = "success" | "warning" | "error" | "ghost";
const STATUS_BADGE: Record<string, { label: string; variant: BadgeVariant }> = {
  ACTIVE: { label: "Connected", variant: "success" },
  INITIATED: { label: "Pending", variant: "warning" },
  INITIALIZING: { label: "Pending", variant: "warning" },
  FAILED: { label: "Failed", variant: "error" },
  EXPIRED: { label: "Expired", variant: "error" },
  REVOKED: { label: "Revoked", variant: "error" },
  INACTIVE: { label: "Inactive", variant: "ghost" },
};

function ToolkitIcon({ slug, className }: { slug: string; className?: string }) {
  const src = TOOLKIT_ICONS[slug] ?? (slug.startsWith("google") ? google : null);
  // object-contain: the Docs/Sheets marks are taller than wide — a square
  // size-* box would stretch them otherwise.
  return src ? (
    <img src={src} alt="" className={cn("object-contain", className)} />
  ) : (
    <BlocksIcon className={className} />
  );
}

export function IntegrationsSection() {
  const { orgId, getToken } = useAuth();
  const { user } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    connections,
    toolkits,
    canRemove,
    canConnectAny,
    loaded,
    loadError,
    connecting,
    removing,
    fetchIntegrations,
    createConnectLink,
    removeIntegration,
  } = useIntegrationsStore();

  // Staff-only extras (Clerk publicMetadata uplodio_member) — shown from the
  // client flag so the input doesn't pop in after the fetch; the server's
  // can_connect_any verdict re-checks on every connect.
  const isStaff = Boolean(
    (user?.publicMetadata as Record<string, unknown> | undefined)?.uplodio_member
  );

  // Runs on mount and again on org switch — the list is org-scoped via the token.
  useEffect(() => {
    void (async () => {
      const token = await getToken();
      if (token) await fetchIntegrations(token);
    })();
  }, [getToken, fetchIntegrations, orgId]);

  // Composio's OAuth callback lands back here with ?status=success|failed
  // (plus ids we don't need) — toast once and clean the URL.
  useEffect(() => {
    const status = searchParams.get("status");
    if (!status) return;
    if (status === "success") toast.success("Account connected — Amy can use it now.");
    else if (status === "failed") toast.error("Connecting the account failed. Try again.");
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleConnect = async (toolkit: string, authMode?: EveAuthMode) => {
    const slug = toolkit.trim().toLowerCase();
    if (!slug) return;
    const token = await getToken();
    if (!token) return;
    const url = await createConnectLink(slug, token, authMode);
    // Same tab: the provider's consent screen returns to this page.
    if (url) window.location.assign(url);
  };

  const handleRemove = async (connectedAccountId: string) => {
    const token = await getToken();
    if (!token) return;
    await removeIntegration(connectedAccountId, token);
  };

  // Skeleton until the first successful fetch (covers the token round-trip
  // before the store flips isLoading) — unless that fetch failed.
  const showSkeleton = !loaded && !loadError;

  return (
    <div className="space-y-8">
      <Frame>
        <FrameHeader>
          <FrameTitle>
            Integrations <FrameCount>{connections.length}</FrameCount>
          </FrameTitle>
          <FrameDescription>The Gmail pool Amy sends from, and tools she can act in.</FrameDescription>
          <FrameAction>
            <ConnectDialog
              toolkits={toolkits.length ? toolkits : DEFAULT_TOOLKITS}
              allowCustom={isStaff || canConnectAny}
              connecting={connecting}
              onConnect={(slug, authMode) => void handleConnect(slug, authMode)}
            />
          </FrameAction>
        </FrameHeader>

        {showSkeleton && (
          <FramePanel className="p-3">
            <Skeleton className="h-16 w-full rounded-lg" />
          </FramePanel>
        )}

        {!showSkeleton && connections.length > 0 && (
          <Table variant="card">
            <TableHeader>
              <TableRow>
                <TableHead className="w-full">Account</TableHead>
                <TableHead className="w-28">Type</TableHead>
                <TableHead className="w-28">Status</TableHead>
                <TableHead>Used by</TableHead>
                <TableHead className="w-28">Connected</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {connections.map((c) => (
                <IntegrationRow
                  key={c.connected_account_id}
                  connection={c}
                  canRemove={canRemove}
                  busy={connecting !== null || removing !== null}
                  removing={removing === c.connected_account_id}
                  onReconnect={() => void handleConnect(c.toolkit, c.auth_mode ?? undefined)}
                  onRemove={() => void handleRemove(c.connected_account_id)}
                />
              ))}
            </TableBody>
          </Table>
        )}

        {!showSkeleton && loaded && connections.length === 0 && (
          <FramePanel>
            <EmptyState
              className="mb-10"
              icon={<UnplugIcon />}
              text="Nothing connected yet. Connect a Gmail account so Amy has a mailbox to send from."
            />
          </FramePanel>
        )}

        {!loaded && loadError && (
          <FramePanel>
            <EmptyState
              className="mb-10"
              icon={<PlugIcon />}
              text="Couldn't reach Amy to load the connections. Refresh to try again."
            />
          </FramePanel>
        )}

      </Frame>

      <DubIntegrationFrame />
    </div>
  );
}

/**
 * Header "Connect" button → a two-step picker (start-campaign-dialog shape:
 * header, list, footer). Step one lists the toolkits; clicking one mints
 * its connect link right away. Toolkits that offer an auth-mode choice
 * (Gmail) swap the dialog to step two — the same list shape, one row per
 * app (Composio's or Uplodio's own Google client) — where clicking a row
 * connects right away; Back returns to the toolkits. Staff may still type
 * any Composio slug, which needs the footer button.
 */
function ConnectDialog({
  toolkits,
  allowCustom,
  connecting,
  onConnect,
}: {
  toolkits: string[];
  allowCustom: boolean;
  connecting: string | null;
  onConnect: (slug: string, authMode?: EveAuthMode) => void;
}) {
  const [open, setOpen] = useState(false);
  const [customSlug, setCustomSlug] = useState("");
  const [step, setStep] = useState<1 | 2>(1);
  /** Toolkit step two is for — kept while sliding back so the title doesn't flash. */
  const [authToolkit, setAuthToolkit] = useState("gmail");
  const [pendingMode, setPendingMode] = useState<EveAuthMode | null>(null);
  const custom = customSlug.trim().toLowerCase();
  const busy = connecting !== null;

  const pick = (slug: string) => {
    if (AUTH_MODE_TOOLKITS.has(slug)) {
      setCustomSlug("");
      setAuthToolkit(slug);
      setStep(2);
      return;
    }
    onConnect(slug);
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={(nowOpen) => {
        setOpen(nowOpen);
        // Reset on OPEN, not close — resetting on close would slide step one
        // back in while the dialog is still fading out.
        if (nowOpen) {
          setCustomSlug("");
          setStep(1);
          setPendingMode(null);
        }
      }}
    >
      <AlertDialogTrigger asChild>
        <Button size="sm" disabled={busy}>
          {busy ? <Spinner /> : <PlusIcon />}
          Connect
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="max-w-lg">
        {step === 1 ? (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>Connect an account</AlertDialogTitle>
              <AlertDialogDescription>
                Pick a tool to sign in with the provider — you'll land back here.
                Gmail accounts join the mailbox pool Amy sends from.
              </AlertDialogDescription>
            </AlertDialogHeader>

            <ItemList>
              {toolkits.map((t) => (
                <PickerOption
                  key={t}
                  icon={<ToolkitIcon slug={t} className="size-4" />}
                  label={toolkitLabel(t)}
                  description={TOOLKIT_BLURBS[t] ?? "Actions Amy can run for you"}
                  busy={connecting === t}
                  disabled={busy}
                  onClick={() => pick(t)}
                />
              ))}
            </ItemList>

            {allowCustom && (
              <div className="flex flex-col gap-2">
                <Label htmlFor="toolkit_slug">
                  Any Composio toolkit{" "}
                  <span className="text-muted-foreground text-xs font-normal">(Uplodio staff)</span>
                </Label>
                <Input
                  id="toolkit_slug"
                  placeholder="e.g. shopify"
                  value={customSlug}
                  onChange={(e) => setCustomSlug(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && custom && !busy) pick(custom);
                  }}
                  disabled={busy}
                />
              </div>
            )}

            <AlertDialogFooter>
              <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
              {allowCustom && (
                <Button onClick={() => custom && pick(custom)} disabled={!custom || busy}>
                  {connecting === custom ? <Spinner /> : <PlugIcon />}
                  Connect
                </Button>
              )}
            </AlertDialogFooter>
          </>
        ) : (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>Connect {toolkitLabel(authToolkit)}</AlertDialogTitle>
              <AlertDialogDescription>
                Pick which app signs you in. Either way the mailbox joins the
                pool Amy sends from.
              </AlertDialogDescription>
            </AlertDialogHeader>

            <ItemList>
              {AUTH_MODE_OPTIONS.map((option) => (
                <PickerOption
                  key={option.value}
                  icon={<AuthModeMark mode={option.value} className="size-4" />}
                  label={option.label}
                  description={option.description}
                  busy={busy && pendingMode === option.value}
                  disabled={busy}
                  onClick={() => {
                    setPendingMode(option.value);
                    onConnect(authToolkit, option.value);
                  }}
                />
              ))}
            </ItemList>

            <AlertDialogFooter className="sm:justify-between">
              <Button
                variant="ghost"
                disabled={busy}
                onClick={() => {
                  setStep(1);
                  setPendingMode(null);
                }}
              >
                <ArrowLeftIcon />
                Back
              </Button>
              <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** One row of the picker: icon tile + label + blurb. The click IS the action; the tile shows a spinner while its link is minted. */
function PickerOption({
  icon,
  label,
  description,
  busy,
  disabled,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  description: string;
  busy: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <Item variant="row" asChild>
      <button
        type="button"
        className="w-full text-left disabled:pointer-events-none disabled:opacity-50"
        onClick={onClick}
        disabled={disabled}
      >
        <ItemMedia variant="icon">
          {busy ? <Spinner /> : icon}
        </ItemMedia>
        <ItemContent className="gap-0">
          <ItemTitle>{label}</ItemTitle>
          <ItemDescription>{description}</ItemDescription>
        </ItemContent>
      </button>
    </Item>
  );
}

/** How many "used by" chips the cell shows before folding into "+N". */
const USED_BY_PREVIEW = 1;

/**
 * "Used by" cell: a two-chip preview (campaigns first, then wait phases)
 * that opens a small dialog listing everything still depending on the
 * account — each campaign deep-links to its page, since re-pointing the
 * mailbox happens there. The same list is what the server refuses removal
 * over.
 */
function UsedByCell({ connection: c, title }: { connection: EveIntegration; title: string }) {
  const chips = [
    ...c.used_by_campaigns.map((u) => ({ key: `m-${u.id}`, label: u.name })),
    ...c.used_by_gates.map((g) => ({ key: `g-${g.id}-${g.phase}`, label: `${g.name} · ${g.phase}` })),
  ];
  const hidden = chips.length - USED_BY_PREVIEW;

  return (
    <Dialog>
      <DialogTrigger asChild>
        {/* One line, always: the name truncates, the +N never wraps. */}
        <Button
          type="button"
          variant="ghost"
          size="xs"
          className="max-w-48 justify-start gap-1.5 text-xs font-normal"
          title="See everything using this account"
        >
          {chips.slice(0, USED_BY_PREVIEW).map((chip) => (
            <span key={chip.key} className="min-w-0 truncate">
              {chip.label}
            </span>
          ))}
          {hidden > 0 && <span className="shrink-0 font-mono">+{hidden}</span>}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Used by</DialogTitle>
          <DialogDescription>
            Everything that still depends on <span className="font-medium text-foreground">{title}</span>.
            Removing the account is refused until these are re-pointed.
          </DialogDescription>
        </DialogHeader>
        {c.used_by_campaigns.length > 0 && (
          <Frame>
            <FrameHeader>
              <FrameTitle>
                Sends email for {c.used_by_campaigns.length} campaign
                {c.used_by_campaigns.length === 1 ? "" : "s"}
              </FrameTitle>
            </FrameHeader>
            <ItemList>
              {c.used_by_campaigns.map((u) => (
                <UsedByLink key={u.id} to={`/dashboard/campaigns/${u.id}`} icon={<MegaphoneIcon />}>
                  {u.name}
                </UsedByLink>
              ))}
            </ItemList>
          </Frame>
        )}
        {c.used_by_gates.length > 0 && (
          <Frame>
            <FrameHeader>
              <FrameTitle>
                {c.used_by_gates.length} wait phase{c.used_by_gates.length === 1 ? "" : "s"} listening
              </FrameTitle>
            </FrameHeader>
            <ItemList>
              {c.used_by_gates.map((g) => (
                <UsedByLink key={`${g.id}-${g.phase}`} to={`/dashboard/campaigns/${g.id}`} icon={<HourglassIcon />}>
                  {g.name} <span className="text-muted-foreground">· {g.phase}</span>
                </UsedByLink>
              ))}
            </ItemList>
          </Frame>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** A "used by" row: the whole row opens the campaign, where the mailbox is re-pointed. */
function UsedByLink({ to, icon, children }: { to: string; icon: ReactNode; children: ReactNode }) {
  return (
    <Item variant="row" size="sm" className="transition-none" asChild>
      <Link to={to}>
        <ItemMedia className="[&_svg]:size-3.5 [&_svg]:text-muted-foreground">{icon}</ItemMedia>
        <ItemContent>
          <ItemTitle className="w-full truncate font-normal">{children}</ItemTitle>
        </ItemContent>
        <ArrowUpRightIcon className="size-3.5 shrink-0 text-muted-foreground" />
      </Link>
    </Item>
  );
}

/**
 * One table row. Actions live in the three-dot menu: Reconnect (non-ACTIVE
 * rows), Copy account id (what Amy's set_campaign_mailbox accepts), Remove
 * (org admins — disabled while the account is still in use; the server
 * refuses regardless). The confirm dialog is a sibling of the menu, opened
 * from the item's onSelect, so it survives the menu closing.
 */
function IntegrationRow({
  connection: c,
  canRemove,
  busy,
  removing,
  onReconnect,
  onRemove,
}: {
  connection: EveIntegration;
  canRemove: boolean;
  busy: boolean;
  removing: boolean;
  onReconnect: () => void;
  onRemove: () => void;
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const badge = STATUS_BADGE[c.status] ?? { label: c.status.toLowerCase(), variant: "ghost" as const };
  const inUse = c.used_by_campaigns.length > 0 || c.used_by_gates.length > 0;
  const title = c.account_email ?? toolkitLabel(c.toolkit);

  return (
    <TableRow data-context-menu>
      {/* max-w-0: the column takes what's left, so a long address truncates. */}
      <TableCell className="max-w-0 font-medium">
        <div className="flex min-w-0 items-center gap-2">
          <div className="flex size-7 shrink-0 items-center justify-center rounded-md bg-muted [&_svg]:text-muted-foreground">
            <ToolkitIcon slug={c.toolkit} className="size-3.5" />
          </div>
          <TruncatedText className="leading-normal">{title}</TruncatedText>
        </div>
      </TableCell>
      <TableCell className="text-xs font-mono text-muted-foreground">
        {toolkitLabel(c.toolkit)}
        {c.auth_mode && (
          <span className="mt-0.5 flex items-center gap-1 text-[10px] opacity-70">
            <AuthModeMark mode={c.auth_mode} className="size-2.5" />
            {AUTH_MODE_LABELS[c.auth_mode]}
          </span>
        )}
      </TableCell>
      <TableCell>
        <Badge variant={badge.variant}>{badge.label}</Badge>
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">
        {inUse ? <UsedByCell connection={c} title={title} /> : "—"}
      </TableCell>
      <TableCell className="text-xs font-mono text-muted-foreground">
        {c.connected_at ? new Date(c.connected_at).toLocaleDateString() : "—"}
      </TableCell>
      <TableCell>
        <div className="flex justify-end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="muted-ghost"
                size="icon-sm"
               
                aria-label="Connection options"
                disabled={busy}
              >
                {removing ? (
                  <Spinner className="size-3.5" />
                ) : (
                  <MoreVerticalIcon className="size-4" />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {c.status !== "ACTIVE" && (
                <DropdownMenuItem onSelect={onReconnect}>
                  <RefreshCwIcon />
                  Reconnect
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onSelect={() => {
                  void navigator.clipboard.writeText(c.connected_account_id);
                  toast.success("Account id copied.");
                }}
              >
                <CopyIcon />
                Copy account id
              </DropdownMenuItem>
              {canRemove && (
                <>
                  <DropdownMenuSeparator />
                  {inUse && (
                    <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                      Still in use - re-point its campaigns first
                    </DropdownMenuLabel>
                  )}
                  <DropdownMenuItem
                    variant="destructive"
                    disabled={inUse}
                    onSelect={() => setConfirmOpen(true)}
                  >
                    <Trash2Icon />
                    Remove
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
            <AlertDialogContent className="max-w-md">
              <AlertDialogHeader>
                <AlertDialogTitle>Remove {title}?</AlertDialogTitle>
                <AlertDialogDescription>
                  Amy loses access to this account: its tokens are revoked at
                  Composio and it leaves the pool. Reconnect later by connecting
                  it again. This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={onRemove}
                  variant="destructive"
                >
                  <Trash2Icon />
                  Remove
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </TableCell>
    </TableRow>
  );
}
